import { ISegregationInfo } from 'src/app/features/quotations/models/quotations.models';

export const TimelineObjectType = {
  MESSAGE: 'message',
  EVENT: 'event',
  COMMENT: 'comment',
  ORDER: 'order',
  ORDER_PLACEHOLDER: 'order_placeholder',
  /** Client-side only (WB-7598) — never returned by the API. See {@link ITimelineOrderGroup}. */
  ORDER_GROUP: 'order_group',
} as const;
export type TimelineObjectType = (typeof TimelineObjectType)[keyof typeof TimelineObjectType];

/** Message item in the conversation timeline (API shape; id can be number or string) */
export interface ITimelineMessage {
  object_type: typeof TimelineObjectType.MESSAGE;
  id: number | string;
  provider_message_id?: string;
  thread_id: string | number;
  provider?: string;
  subject: string;
  sender: string;
  sender_name: string;
  sender_email: string;
  recipients: string;
  cc?: string;
  bcc?: string | boolean;
  timestamp: string;
  is_read?: boolean;
  read_at?: string | null;
  is_starred?: boolean;
  is_archived?: boolean;
  has_attachments?: boolean;
  attachment_count?: number;
  body_cached_at?: string | null;
  body_cache_key?: boolean;
  message_id?: string;
  deprecated_id?: string;
  folders?: string;
  body: string;
  body_format?: string;
  attachments?: ITimelineAttachment[];
  in_reply_to?: { id: string; message_id: string };
  classification?: string;
  classification_confidence?: number;
  /** Fine-grained intent detected by the classifier (open uppercase string). */
  intent?: string | null;
  intent_confidence?: number;
  /** Who the external correspondent is; absent when never resolved. */
  counterparty?: string | null;
  counterparty_confidence?: number;
  /** What asserted the counterparty. */
  counterparty_source?: string | null;
  /** PII-free justification, shown in the badge tooltip. */
  counterparty_evidence?: string | null;
  /** `confirmed` / `to_verify` / `none` — derived server-side, never stored. */
  counterparty_status?: string | null;
  /** The AI's own read of this message; never the verdict. */
  counterparty_model?: string | null;
  counterparty_model_confidence?: number;
  /** The AI disagrees above its threshold; computed server-side. */
  counterparty_conflict?: boolean;
  /** The correspondent was declared to play both sides. */
  counterparty_dual_role?: boolean;
  /** The AI's own urgency judgment for this message (thread urgency is separate). */
  is_urgent?: boolean;
  /** Why the AI classified/flagged this message, already in the user's language. */
  reasoning?: string;
  sent_by_automation?: boolean;
  automation_name?: string | null;
}

export const TimelineEventType = {
  CATEGORY_CHANGE: 'category_change',
  INTENT_CHANGE: 'intent_change',
  COUNTERPARTY_CHANGE: 'counterparty_change',
  COUNTERPARTY_DUAL_ROLE: 'counterparty_dual_role',
  URGENCY_CHANGE: 'urgency_change',
  STATUS_CHANGE: 'task_status_change',
  ASSIGNEE_CHANGE: 'assignment_change',
  CUSTOMER_CHANGE: 'customer_change',
  EMAIL_FORWARDED: 'email_forwarded',
  EMAIL_FORWARD_FAILED: 'email_forward_failed',
  REPLY_DRAFTED: 'reply_drafted',
  ORDER_CREATED: 'order_created',
  ORDER_CREATION_FAILED: 'order_creation_failed',
  LABEL_APPLIED: 'label_applied',
  LABEL_REMOVED: 'label_removed',
} as const;
export type TimelineEventType = (typeof TimelineEventType)[keyof typeof TimelineEventType];

/**
 * Mirrors the four values of `wolsal.email.thread.event.source`.
 *
 * `ai` and `provider_sync` shipped server-side in Phase 55 and this union was
 * never widened to match. Measured 2026-09-08: **no production code writes
 * either value yet** -- they appear only in backend tests, because AI labelling
 * and provider sync are later work. So this is a LATENT gap, not a live one:
 * the first such event would arrive typed as `user`/`automation` by omission,
 * and (before the nullable `user` fix in the same phase) would have crashed the
 * timeline render outright. Widened here because PROV-01 needs all four
 * distinguishable, and because the fix is cheaper before the data exists.
 *
 * Neither `ai` nor `provider_sync` may carry a `user_id` — the model's
 * `_check_source_and_actor_agree` refuses it — so `ITimelineEvent.user` is
 * empty on those rows and anything rendering an actor has to name the source
 * instead. See `getTimelineActorLabel`.
 */
export const TimelineEventSource = {
  USER: 'user',
  AUTOMATION: 'automation',
  AI: 'ai',
  PROVIDER_SYNC: 'provider_sync',
} as const;
export type TimelineEventSource =
  (typeof TimelineEventSource)[keyof typeof TimelineEventSource];

/**
 * Display filter for the conversation-detail timeline ("View" preference).
 *   • FULL     – every timeline object (messages, orders, events, comments)
 *   • EMAILS   – email messages only
 *   • COMMENTS – internal comments only
 *   • SIMPLE   – hides system events and anything sent by an automation
 */
export const TimelineViewMode = {
  FULL: 'full',
  EMAILS: 'emails',
  COMMENTS: 'comments',
  SIMPLE: 'simple',
} as const;
export type TimelineViewMode =
  (typeof TimelineViewMode)[keyof typeof TimelineViewMode];

export interface ITimelineEventUser {
  id: number;
  name: string;
}

export interface ITimelineEvent {
  object_type: typeof TimelineObjectType.EVENT;
  id: number;
  thread_id: number;
  provider_message_id?: string;
  event_type: TimelineEventType;
  /**
   * Null on an `ai` or `provider_sync` row: the model refuses those a `user_id`,
   * so the key arrives empty. Typed non-nullable until 56.5-02, which is how
   * `getUserFullname(null)` reached a `TypeError` and took the whole timeline
   * render down with it.
   */
  user: ITimelineEventUser | null;
  message: string;
  old_value: string | null;
  new_value: string | null;
  source?: TimelineEventSource;
  automation_name?: string | null;
  draft_id?: number | null;
  created_at: string;
}

export interface ITimelineAttachment {
  id: number;
  name: string;
  size: number;
  download_url: string;
}

export interface ITimelineComment {
  object_type: typeof TimelineObjectType.COMMENT;
  author: { id: number; name: string; email: string };
  body: string;
  created_at: string;
  id: number;
  mentions: { id: number; name: string; email: string }[];
  my_reactions: { id: number; name: string; email: string }[];
  reactions: { id: number; name: string; email: string }[];
  thread_id: number;
  source?: TimelineEventSource;
  automation_name?: string | null;
  updated_at: string;
}

/**
 * One line of the order artifact's line-item table. Served inline by the thread
 * payload (WB-7444); for payloads issued before it shipped the artifact derives
 * it from `GET /v1/quotations/{id}` (see `mapQuotationLineToPreview`).
 */
export interface IOrderLinePreview {
  id: number;
  product_name: string;
  sku: string;
  quantity: number;
  /** Quantity expressed in the selected handling unit, when the line has one. */
  quantity_handling_unit?: number | null;
  /** Name of the selected handling unit, e.g. 'BOX'. */
  handling_unit_name?: string | null;
  /**
   * The effective price. `price_unit` is the catalog price in the order detail.
   * Null on an unmatched row: no product was resolved, so no price exists.
   */
  final_price: number | null;
  total: number | null;
  /**
   * The AI could not match this row, so it is not an order line at all — it
   * lives in `ai_unmatched_order_lines` and only the side-by-side can resolve
   * it. Drives the alert icon and the card's Review action.
   */
  unmatched?: boolean;
}

export interface ITimelineOrder {
  object_type: typeof TimelineObjectType.ORDER;
  id: number;
  order: {
    id: number;
    name: string;
    state: string;
    created_at: string;
    customer_name: string;
    total: number;
    currency: { name: string; symbol: string; position: string };
    /** WB-7443. Optional: payloads issued before it shipped fall back to `created_at`. */
    last_modified?: string;
    /** WB-7444 — capped server-side; absent on older payloads, fetched client-side then. */
    lines_preview?: IOrderLinePreview[];
    /** WB-7444 — count of every real line, drives the `+N more` link. */
    total_order_lines?: number;
    /**
     * WB-7024 — the total the order detail prints: `total` above sums every
     * line, this one only the priced ones. Read it first, or the card and the
     * side-by-side disagree on an order mixing priced and no-price lines.
     */
    priced_lines_total?: number;
    /** WB-7024 semantics — render 'No price' instead of a total. */
    all_lines_no_price?: boolean;
    /** WB-7024 snapshot — hide the Price / Total columns. */
    hide_pricing_columns?: boolean;
    /** Rows still waiting to be matched. Non-zero makes the card offer Review. */
    unmatched_lines_count?: number;
    /**
     * The order's own company and location, the two dedicated stored fields.
     * Null once the backend says there is none, absent on a payload issued
     * before they shipped — the card must not conflate the two.
     *
     * Not `customer_name` above: that one collapses to the location alone when
     * there is no company, so it cannot answer "is the company missing?".
     */
    company_name?: string | null;
    location_name?: string | null;
  };
  author: { id: number; name: string; email: string };
  thread_id: number;
  created_at: string;
  updated_at: string;
  /**
   * WB-7599 — the message the order was linked to. Every order produced by one
   * create-order run carries the same value, which makes it the grouping key of
   * the "N Orders created" artifact (WB-7598).
   *
   * Sits on the timeline entry, beside `author` and `created_at`, not inside
   * `order` — that is where the backend writes it. Absent on threads created
   * before it shipped; those render as single cards.
   */
  message_id?: number | null;
  /**
   * WB-7599 — why this order exists as a separate order, when a segregation rule
   * split it off. Same shape the quotation detail already receives. Null for an
   * AI multi-order PDF split and for a plain single order.
   */
  segregation_info?: ISegregationInfo | null;
  /**
   * WB-7599 — the split label carried by the order itself. Read it before
   * `segregation_info`: the AI create-order flow splits with `split_in_place`,
   * which stamps every order of the run with a label but leaves the source
   * without the `segregation_source_id` `segregation_info` is built from — so a
   * group's source row would show no badge at all if read from there.
   */
  segregation_split_label?: string | null;
}

/** Synthetic timeline item shown while create-order API is in progress. */
export const ORDER_PLACEHOLDER_ID = 'order-placeholder';

export interface ITimelineOrderPlaceholder {
  object_type: typeof TimelineObjectType.ORDER_PLACEHOLDER;
  id: typeof ORDER_PLACEHOLDER_ID;
}

/**
 * The N orders one create-order run produced, collapsed into a single timeline
 * artifact (WB-7598).
 *
 * Synthesised client-side by `groupTimelineOrders` — the API keeps returning one
 * `order` entry per order. Only built for 2+ orders sharing a `message_id`; a
 * lone order stays an {@link ITimelineOrder} and renders the card it always did.
 */
export interface ITimelineOrderGroup {
  object_type: typeof TimelineObjectType.ORDER_GROUP;
  /** Stable across refetches — `trackByTimelineItem` keys the timeline on it. */
  id: string;
  /** The shared `message_id`, i.e. the run these orders came out of. */
  message_id: number;
  /** In API order (by `created_at`). Always 2 or more. */
  orders: ITimelineOrder[];
}

export type IConversationTimelineObject =
  | ITimelineMessage
  | ITimelineEvent
  | ITimelineComment
  | ITimelineOrder
  | ITimelineOrderPlaceholder
  | ITimelineOrderGroup;

export function isTimelineMessage(obj: IConversationTimelineObject): obj is ITimelineMessage {
  return obj.object_type === TimelineObjectType.MESSAGE;
}

export function isTimelineEvent(obj: IConversationTimelineObject): obj is ITimelineEvent {
  return obj.object_type === TimelineObjectType.EVENT;
}

export function isTimelineComment(obj: IConversationTimelineObject): obj is ITimelineComment {
  return obj.object_type === TimelineObjectType.COMMENT;
}

export function isTimelineOrder(obj: IConversationTimelineObject): obj is ITimelineOrder {
  return obj.object_type === TimelineObjectType.ORDER;
}

export function isTimelineOrderPlaceholder(obj: IConversationTimelineObject): obj is ITimelineOrderPlaceholder {
  return obj.object_type === TimelineObjectType.ORDER_PLACEHOLDER;
}

export function isTimelineOrderGroup(obj: IConversationTimelineObject): obj is ITimelineOrderGroup {
  return obj.object_type === TimelineObjectType.ORDER_GROUP;
}